import clsx from "clsx";
import { sortBy } from "lodash";

import { arrayEqualityFn, useAppSelector } from "hooks/redux_hooks";
import { PollSessionStatusType, PollTypeType } from "shared/types";
import { classroomSelectors } from "store/index";
import { numberToLetter } from "utils/string";

const CHOICE_COLORS = [
  "red-violet",
  "orange",
  "blue-green-shade-20",
  "violet",
  "black-tint-40",
  "blue-shade-40",
  "blue-tint-20",
  "black-tint-90",
  "red-violet-shade-40",
  "yellow",
  "yellow-green",
  "green-shade-40",
];
const DEFAULT_COLOR = "blue-shade-20";

function PollResponsesIndicator({
  userId,
  currentUserCanControlClassroom,
  showBar,
  showDropdown,
  index,
  isVertical = false,
  transformTopRight = false,
}: {
  userId: string;
  currentUserCanControlClassroom: boolean;
  showBar: boolean;
  showDropdown: boolean;
  index: number;
  isVertical?: boolean;
  transformTopRight?: boolean;
}) {
  const [pollResponsesForBar, pollResponsesForDropdown] = useAppSelector(
    classroomSelectors.selectPollResponsesForPollResponsesIndicator(
      userId,
      currentUserCanControlClassroom,
    ),
    (a, b) => arrayEqualityFn(a[0], b[0]) && arrayEqualityFn(a[1], b[1]),
  );
  const canSeeDropdown = showDropdown && pollResponsesForDropdown.length > 0;

  if (pollResponsesForBar.length === 0 && pollResponsesForDropdown.length === 0) return null;

  return (
    <>
      <div
        className="absolute bottom-0 left-0 right-0 flex"
        style={
          showBar
            ? {
                height: "3px",
                zIndex: 101,
                opacity: 1,
                transition: "opacity 0.15s ease-in-out",
                transitionDelay: "0.15s",
              }
            : {
                opacity: 0,
                visibility: "hidden",
              }
        }
        data-testid={`pri-bar-${userId}`}
      >
        {pollResponsesForBar.map((response) => {
          const pollSessionId = response.poll_session_id;

          if (response.poll.type === PollTypeType.LongForm) {
            return (
              <span
                key={`pri-bar-item-${pollSessionId}`}
                className={`flex-auto bg-${DEFAULT_COLOR}`}
                style={{ height: "3px" }}
                data-testid={`pri-bar-item-${pollSessionId}`}
              />
            );
          }

          // This means the MC/MR poll is still in progress and the user's response is hidden
          if (typeof response.response === "string") {
            return (
              <span
                key={`pri-bar-item-${pollSessionId}`}
                className={`flex-auto bg-${DEFAULT_COLOR}`}
                style={{ height: "3px" }}
                data-testid={`pri-bar-item-${pollSessionId}`}
              />
            );
          }
          const choices = ([...response.response] as number[]).sort();
          return choices.map((choiceIndex) => {
            const color =
              currentUserCanControlClassroom ||
              (response.poll_session.status === PollSessionStatusType.Completed &&
                response.poll.student_visible)
                ? CHOICE_COLORS[choiceIndex % CHOICE_COLORS.length]
                : DEFAULT_COLOR;
            return (
              <span
                key={`pri-bar-item-${pollSessionId}-${choiceIndex}`}
                className={`flex-auto bg-${color}`}
                style={{ height: "3px" }}
                data-testid={`pri-bar-item-${pollSessionId}-${choiceIndex}`}
              />
            );
          });
        })}
      </div>
      <div
        className="absolute bg-black-tint-20 body-s text-black-tint-90 border-box shadow-level-3"
        style={
          canSeeDropdown && isVertical
            ? {
                top: index === 0 ? 0 : "unset",
                bottom: index !== 0 ? 0 : "unset",
                left: "100%",
                width: "calc(267px / var(--participant-scale))",
                fontSize: "calc(14px / var(--participant-scale))",
                opacity: 1,
                transition: "opacity 0.15s ease-in-out",
                transitionDelay: "0.15s",
                padding:
                  "calc(8px / var(--participant-scale)) calc(8px / var(--participant-scale)) calc(4px / var(--participant-scale))",
              }
            : canSeeDropdown && !isVertical
              ? {
                  top: "100%",
                  right: transformTopRight ? 0 : "unset",
                  width: "100%",
                  fontSize: "calc(14px / var(--participant-scale))",
                  opacity: 1,
                  transition: "opacity 0.15s ease-in-out",
                  transitionDelay: "0.15s",
                  padding:
                    "calc(8px / var(--participant-scale)) calc(8px / var(--participant-scale)) calc(4px / var(--participant-scale))",
                }
              : {
                  opacity: 0,
                  visibility: "hidden",
                }
        }
        data-testid={`pri-dropdown-${userId}`}
      >
        {pollResponsesForDropdown.map((response, pollResponseIndex) => {
          const pollSessionId = response.poll_session_id;

          if (response.poll.type === PollTypeType.LongForm) {
            return (
              <div
                key={`pri-dropdown-item-${pollSessionId}-${userId}`}
                style={{
                  borderTop: pollResponseIndex !== 0 ? "1px solid var(--black-tint-40)" : "none",
                  borderWidth: "calc(1px / var(--participant-scale))",
                  padding: "calc(8px / var(--participant-scale))",
                }}
              >
                <div>{response.response}</div>
              </div>
            );
          }

          const choices = sortBy(response.poll.poll_choices, "index");
          const selectedChoices = ([...response.response] as number[]).sort();
          return (
            <div
              key={`pri-dropdown-item-${pollSessionId}-${userId}`}
              style={{
                borderTop: pollResponseIndex !== 0 ? "1px solid var(--black-tint-40)" : "none",
                borderWidth: "calc(1px / var(--participant-scale))",
                padding: "calc(8px / var(--participant-scale))",
              }}
            >
              {selectedChoices.map((choice, choiceIndex) => {
                const color = CHOICE_COLORS[choice % CHOICE_COLORS.length];
                return (
                  <div
                    key={`pri-dropdown-item-${pollSessionId}-${userId}-${choice}`}
                    className={clsx("flex items-center", {
                      pt1: choiceIndex !== 0,
                    })}
                  >
                    <span
                      className={`circle flex-none bg-${color}`}
                      style={{
                        width: "calc(12px / var(--participant-scale))",
                        height: "calc(12px / var(--participant-scale))",
                        marginRight: "calc(8px / var(--participant-scale))",
                      }}
                    />
                    <span>
                      {numberToLetter(choice)}. {choices[choice].choice}
                    </span>
                  </div>
                );
              })}
            </div>
          );
        })}
      </div>
    </>
  );
}

export { PollResponsesIndicator };
